<template>
	<b-modal id="app-update-version" v-model="isUpdateNeeded" no-close-on-backdrop no-close-on-esc hide-header centered>
		<b-container v-if="mode === 'verbose'" class="text-slate-600 fw-400">
			<b-row>
				<b-col v-if="stepsUpdateIsInProcess" cols="12" class="gap-15 d-flex align-items-center fs-16">
					<div>
						<component :is="getLucideIcon('AlertTriangle')" :size="48" :stroke-width="1.5" />
					</div>
					<div v-if="isUpdating">{{ FormMSG(5, 'Updating in progress, please wait a moment...') }}</div>
					<div v-else>{{ FormMSG(6, 'A new version of the app was detected, Please take a few moments to start the update, Thanks.') }}</div>
				</b-col>
				<b-col v-else cols="12" class="gap-15 d-flex align-items-center fs-16">
					<div>
						<component :is="getLucideIcon('CheckCheck')" :size="48" :stroke-width="1.5" class="text-success" />
					</div>
					<div>{{ FormMSG(7, 'Update complete. Enjoy! (your app will reload in order to apply the changes.)') }}</div>
				</b-col>
			</b-row>
			<section v-if="isUpdating">
				<!-- eslint-disable-next-line vue/html-self-closing -->
				<hr />
				<b-row v-for="(step, index) in updateSteps" :key="`step-${index}`">
					<b-col cols="12" class="gap-15 d-flex align-items-center fs-14 py-2">
						<div v-if="step.inProcess"><b-spinner small /></div>
						<div v-else>
							<component :is="getLucideIcon('Check')" v-if="step.success" :size="18" :stroke-width="1.5" class="text-success" />
							<component :is="getLucideIcon('X')" v-else :size="18" :stroke-width="1.5" class="text-danger" />
						</div>
						<div>{{ step.msg }}</div>
					</b-col>
				</b-row>
				<b-row v-if="!stepsUpdateIsInProcess && stepsUpdateFailureExists">
					<b-col cols="12" class="gap-15 d-flex align-items-center fs-14 py-2 text-danger">
						<span>
							{{
								FormMSG(
									8,
									`Some steps failed during the update process, you may need to reload your entire screen with the "CTRL+F5" key to resolve the failures.`
								)
							}}
						</span>
					</b-col>
				</b-row>
			</section>
		</b-container>
		<b-container v-if="mode === 'simple'" class="text-slate-600 fw-400">
			<b-row>
				<b-col cols="12" class="gap-15 d-flex align-items-center fs-18 custom-lh-lg text-justify">
					<div v-if="isNotConnected" class="m-3">
						{{ FormMSG(13, 'New application version detected, you need to reload first in order to fully apply the update before Sign in.') }}
					</div>
					<div v-else class="m-3">
						{{
							FormMSG(11, 'The application has been updated, you will be automatically signed out. Please sign in again to view the new version.')
						}}
					</div>
				</b-col>
			</b-row>
		</b-container>
		<template #modal-footer>
			<b-row v-if="mode === 'verbose'" align-v="center" align-h="end">
				<b-col v-if="updateSteps.length === 0 || stepsUpdateIsInProcess">
					<b-button variant="light" class="w-138-px" :disabled="isUpdating" @click="startUpdate">{{ FormMSG(9, 'Start') }}</b-button>
				</b-col>
				<b-col v-else>
					<b-button variant="success" class="w-138-px" @click="finishUpdate">{{ FormMSG(10, 'Finish') }}</b-button>
				</b-col>
			</b-row>
			<b-row v-if="mode === 'simple'" align-v="center" align-h="end">
				<b-col>
					<b-button variant="dark" class="w-138-px" @click="reload">{{ FormMSG(12, 'Reload') }}</b-button>
				</b-col>
			</b-row>
		</template>
	</b-modal>
</template>

<script>
import LanguageMessages from '@/mixins/languageMessages';
import GlobalMixin from '@/mixins/global.mixin';
import { mapGetters, mapActions } from 'vuex';
import { store } from '@/store';
import { PAGES_NOT_NEED_CHECK_VERSION } from '@/shared/constants';

export default {
	name: 'AppUpdateVersionModal',
	mixins: [LanguageMessages, GlobalMixin],
	props: {
		/**
		 * mode value:
		 * simple ( for direct logout user and fetch update without handling redirect origin behaviour )
		 * verbose ( for hard refresh the page without log user out //need to find how to handle the redirect behaviour after update )
		 */
		mode: {
			type: String,
			required: false,
			default: 'simple'
		}
	},
	data() {
		return {
			isUpdating: false,
			updateSteps: [],
			PAGES_NOT_NEED_CHECK_VERSION
		};
	},
	computed: {
		...mapGetters({
			isUpdateNeeded: 'version/isUpdateNeeded',
			getClientVersion: 'version/getClientVersion'
		}),
		isNotConnected() {
			return PAGES_NOT_NEED_CHECK_VERSION.some((text) => this.$route.fullPath.includes(text));
		},
		stepsUpdateIsInProcess() {
			return this.updateSteps.some((step) => step.inProcess);
		},
		stepsUpdateFailureExists() {
			return this.updateSteps.some((step) => !step.success);
		}
	},
	created() {
		this.isUpdating = false;
		this.updateSteps = [
			{
				msg: this.FormMSG(1, 'Refreshing browser data'),
				inProcess: true,
				success: false
			},
			{
				msg: this.FormMSG(2, 'Updating languages modules'),
				inProcess: true,
				success: false
			},
			{
				msg: this.FormMSG(3, 'Refreshing App configurations'),
				inProcess: true,
				success: false
			},
			{
				msg: this.FormMSG(4, 'Reset App caches'),
				inProcess: true,
				success: false
			}
		];
	},
	methods: {
		/**
         * now, we use only the mode = simple, to make the user logout after update was detected.

         * in order to be able to use the mode = verbose,
         * TO DO: all methods startUpdate, finishUpdate works very well.
         * We need just to find a way on how to handle the HARD REFRESH behaviour on server,
         * because without resolving that issue, on redirect location.href = location.origin,
         * it always wipe out the localStorage and replace it by a new empty one. ( cause: same as cross origin ).
         */
		...mapActions({
			setNewVersionStates: 'version/setNewVersionStates',
			refreshToken: 'refreshToken'
		}),
		async startUpdate() {
			this.isUpdating = true;

			// step 1: refreshToken
			await this.refreshToken();
			const newToken = store.getToken();
			if (newToken) this.updateSteps[0].success = true;
			this.updateSteps[0].inProcess = false;

			// step 2: reload languages & menu.
			try {
				await store.reloadLanguages();
				this.updateSteps[1].success = true;
			} catch (error) {
				console.log('error on reload Languages when updating Apps >>>> ', error);
			} finally {
				this.updateSteps[1].inProcess = false;
			}

			// step 3: reload projects & profile configs
			try {
				await store.onLoad();
				this.updateSteps[2].success = true;
			} catch (error) {
				console.log('error on reload configurations when updating Apps >>>> ', error);
			} finally {
				this.updateSteps[2].inProcess = false;
			}

			// step 4: reset apollo cache
			try {
				await this.$apollo.getClient().cache.reset();
				this.updateSteps[3].success = true;
			} catch (error) {
				console.log('error on reset apollo cache when updating Apps >>>> ', error);
			} finally {
				this.updateSteps[3].inProcess = false;
			}

			// final step: config path for the redirection after finish
			await store.setVersionPathStatus({ fullUpdate: true, lastPath: this.$route.path, query: this.$route.query });
		},
		async finishUpdate() {
			await this.setNewVersionStates({ version: this.getClientVersion, needUpdate: false });
			this.isUpdating = false;

			// force reload screen
			location.href = location.origin;
		},
		async reload() {
			await this.setNewVersionStates({ version: this.getClientVersion, needUpdate: false });
			await store.onLogout();
		}
	}
};
</script>

<style lang="scss" scoped>
.text-slate-600 {
	color: oklch(44.6% 0.043 257.281);
}
.gap-15 {
	gap: 15px;
}
.custom-lh-lg {
	line-height: 1.8;
}
</style>
